import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../components/auth/AuthContext";
import { useLicense } from "../licensing/LicenseContext";
import { HeaderBar } from "../components/layout/TopBar";
import NavBar from "../components/layout/NavBar";
import { FolderTreeArt } from "../components/adminSurface/heroArt";
import {
  getDatabases,
  getFolders, createFolder, updateFolder, moveFolder, archiveFolder, restoreFolder,
  triggerFolderBackfill, getFolderBackfillStatus, cancelFolderBackfill,
  getTaxonomyNodes,
  getWorkspaceTemplate,
  createWorkspaceTemplateFolder,
  updateWorkspaceTemplateFolder,
  archiveWorkspaceTemplateFolder,
  seedWorkspaceTemplate, createTaxonomyNode, updateTaxonomyNode, moveTaxonomyNode, archiveTaxonomyNode, restoreTaxonomyNode,
  getContentTypes, createContentType, updateContentType, archiveContentType,
  getBaseContentType, setBaseContentType,
  getDocumentFolder, setDocumentFolder, getFolderDocuments,
  getDocumentTerms, addDocumentTerm, removeDocumentTerm,
  getTemplates, getTemplate, applyTemplate,
  getContentOrgSummary, clearContentOrg,
} from "../api/adminApi";

const dbId = (d) => d?.id ?? d?.Id ?? d?.databaseId ?? d?.DatabaseId;
const dbName = (d) => d?.dbDisplayName ?? d?.DbDisplayName ?? d?.displayName ?? d?.name ?? d?.dbName ?? d?.DbName ?? String(dbId(d));

// Bespoke "newgen" governance visual language (indigo→violet gradient hero, frosted composer
// cards, focus-ring inputs, custom dropdown/switch, animated status). Mirrors the Legal Holds /
// Disposition pages via a scoped co-* block so nothing global is touched.
const CO_STYLES = `
.co-hero{ position:relative; overflow:hidden; border-radius:18px; padding:24px 28px; margin-bottom:20px;
  background:linear-gradient(135deg,#312e81 0%,#4338ca 46%,#6d28d9 100%);
  box-shadow:0 14px 38px -10px rgba(67,56,202,.55); display:flex; align-items:center; gap:20px; }
.co-hero__glow{ position:absolute; inset:0; pointer-events:none;
  background:radial-gradient(ellipse at 80% 25%, rgba(255,255,255,.16) 0%, transparent 55%); }
.co-hero__logo{ position:relative; flex:0 0 auto; display:flex; align-items:center; pointer-events:none; }
.co-hero__logo svg{ display:block; height:88px; width:auto; }
@media (max-width:820px){ .co-hero__logo{ display:none; } }
.co-hero__body{ position:relative; flex:1; min-width:0; }
.co-hero__title{ margin:0; font-size:23px; font-weight:800; color:#fff; letter-spacing:-.01em; }
.co-hero__sub{ margin:5px 0 0; font-size:13px; color:rgba(255,255,255,.78); }
.co-hero__stat{ position:relative; display:flex; flex-direction:column; align-items:flex-end; padding-right:4px; }
.co-hero__stat b{ font-size:28px; font-weight:800; color:#fff; line-height:1; }
.co-hero__stat span{ display:inline-flex; align-items:center; gap:6px; font-size:11px; text-transform:uppercase;
  letter-spacing:.08em; color:rgba(255,255,255,.72); margin-top:6px; }
.co-hero__dot{ width:7px; height:7px; border-radius:50%; background:#4ade80; animation:co-pulse 1.9s ease-in-out infinite; }

.co-tabs{ display:inline-flex; background:#f1f5f9; border:1px solid #e6eaf1; border-radius:12px; padding:3px; gap:2px; margin-bottom:18px; }
.co-tabs button{ appearance:none; border:none; background:transparent; color:#475569; font-size:13px; font-weight:600;
  padding:9px 18px; border-radius:9px; cursor:pointer; transition:all .15s; white-space:nowrap; }
.co-tabs button:hover{ color:#1e293b; }
.co-tabs button.on{ background:#fff; color:#6d28d9; box-shadow:0 1px 3px rgba(15,23,42,.14); }

.co-card{ background:#fff; border:1px solid #eef0f5; border-radius:16px; padding:20px 22px; margin-bottom:16px;
  box-shadow:0 10px 34px -14px rgba(15,23,42,.14); }
.co-card__title{ font-size:11px; font-weight:800; color:#6d28d9; text-transform:uppercase; letter-spacing:.09em; margin-bottom:15px;
  display:flex; align-items:center; gap:8px; }
.co-grid{ display:grid; gap:14px; align-items:end; }
.co-field{ display:flex; flex-direction:column; min-width:0; }
.co-lbl{ font-size:10.5px; font-weight:700; color:#64748b; text-transform:uppercase; letter-spacing:.07em; margin-bottom:6px; }
.co-input{ width:100%; box-sizing:border-box; padding:10px 12px; border-radius:10px; border:1px solid #e2e8f0;
  font-size:13.5px; font-family:inherit; background:#fff; color:#0f172a; transition:border-color .15s, box-shadow .15s; }
.co-input:hover{ border-color:#cbd5e1; }
.co-input:focus{ outline:none; border-color:#7c3aed; box-shadow:0 0 0 3px rgba(124,58,237,.15); }
.co-input::placeholder{ color:#94a3b8; }
.co-color{ padding:3px; width:52px; height:40px; cursor:pointer; }

.co-cta{ appearance:none; border:none; cursor:pointer; color:#fff; font-weight:700; font-size:13.5px; padding:11px 22px;
  border-radius:12px; background:linear-gradient(135deg,#4338ca,#6d28d9 55%,#7c3aed);
  box-shadow:0 8px 20px -4px rgba(109,40,217,.42); transition:filter .15s, box-shadow .15s, transform .12s;
  display:inline-flex; align-items:center; gap:8px; }
.co-cta:hover{ filter:brightness(1.06); box-shadow:0 10px 26px -4px rgba(109,40,217,.5); }
.co-cta:active{ transform:translateY(1px); }
.co-cta:disabled{ opacity:.55; cursor:not-allowed; filter:none; box-shadow:none; }
.co-ghost{ appearance:none; border:1px solid #e2e8f0; background:#fff; color:#475569; font-weight:600; font-size:13px;
  padding:10px 16px; border-radius:11px; cursor:pointer; transition:all .13s; }
.co-ghost:hover{ border-color:#c4b5fd; color:#6d28d9; background:#faf7ff; }

.co-tablecard{ background:#fff; border:1px solid #eef0f5; border-radius:16px; overflow:hidden;
  box-shadow:0 10px 34px -14px rgba(15,23,42,.12); }
.co-node{ display:flex; align-items:center; gap:11px; padding:11px 16px; border-bottom:1px solid #f4f4f8; transition:background .12s; }
.co-node:last-child{ border-bottom:none; }
.co-node:hover{ background:#faf7ff; }
.co-node__rail{ flex:0 0 auto; align-self:stretch; border-left:2px solid #ece9f8; }
.co-node__ico{ width:30px; height:30px; border-radius:9px; display:flex; align-items:center; justify-content:center; font-size:15px;
  background:linear-gradient(135deg,#eef2ff,#f5f0ff); border:1px solid #eae7fb; flex:0 0 auto; }
.co-node.arch .co-node__ico{ filter:grayscale(1); opacity:.5; }
.co-node__name{ font-weight:600; color:#1e293b; }
.co-node.arch .co-node__name{ color:#94a3b8; }
.co-node__path{ font-size:11.5px; color:#a3adba; margin-left:9px; }
.co-node__actions{ margin-left:auto; display:flex; gap:6px; align-items:center; }
.co-btn{ appearance:none; border:1px solid #e6e8ef; background:#fff; color:#64748b; font-size:12px; font-weight:600;
  padding:6px 11px; border-radius:8px; cursor:pointer; transition:all .13s; white-space:nowrap; }
.co-btn:hover{ border-color:#c4b5fd; color:#6d28d9; background:#faf7ff; }
.co-swatch{ width:12px; height:12px; border-radius:4px; flex:0 0 auto; box-shadow:0 0 0 1px rgba(15,23,42,.08); }

.co-chip{ display:inline-flex; align-items:center; gap:6px; padding:4px 10px; border-radius:8px; font-size:12px; font-weight:600;
  background:#f4f2fb; color:#5b21b6; border:1px solid #ece7fa; white-space:nowrap; }
.co-chip__x{ border:none; background:transparent; color:#a78bfa; cursor:pointer; font-size:12px; line-height:1; padding:0; }
.co-chip__x:hover{ color:#6d28d9; }

.co-status{ display:inline-flex; align-items:center; gap:6px; padding:3px 11px 3px 9px; border-radius:999px;
  font-size:11px; font-weight:700; letter-spacing:.02em; }
.co-status--on{ background:#dcfce7; color:#15803d; }
.co-status--warn{ background:#fef3c7; color:#b45309; }
.co-status--err{ background:#fee2e2; color:#b91c1c; }
.co-status--off{ background:#f1f5f9; color:#64748b; }
.co-status__dot{ width:6px; height:6px; border-radius:50%; background:currentColor; }
.co-status--warn .co-status__dot{ animation:co-pulse 1.4s ease-in-out infinite; }
@keyframes co-pulse{ 0%,100%{opacity:1; transform:scale(1);} 50%{opacity:.35; transform:scale(.8);} }

.co-toolbar{ display:flex; gap:14px; align-items:center; flex-wrap:wrap; background:#fff; border:1px solid #eef0f5;
  border-radius:14px; padding:12px 16px; margin-bottom:14px; box-shadow:0 8px 26px -16px rgba(15,23,42,.16); }
.co-count{ margin-left:auto; display:inline-flex; align-items:center; gap:6px; font-size:12px; color:#5b21b6;
  background:#f4f2fb; border:1px solid #ece7fa; padding:6px 12px; border-radius:999px; font-weight:700; }

.co-empty{ padding:60px 40px; text-align:center; }
.co-empty__ring{ width:76px; height:76px; margin:0 auto 16px; border-radius:22px; display:flex; align-items:center;
  justify-content:center; font-size:34px; background:linear-gradient(135deg,#eef2ff,#f5f0ff); border:1px solid #eae7fb; }
.co-empty__title{ font-size:15px; font-weight:700; color:#334155; }
.co-empty__hint{ font-size:13px; color:#94a3b8; margin-top:5px; }
.co-hint{ font-size:12.5px; color:#94a3b8; line-height:1.5; }
.co-doctable{ width:100%; border-collapse:collapse; font-size:13px; }
.co-doctable th{ text-align:left; padding:11px 16px; font-size:10.5px; font-weight:700; color:#64748b;
  text-transform:uppercase; letter-spacing:.06em; background:#f8fafc; border-bottom:1px solid #eef0f5; }
.co-doctable td{ padding:11px 16px; color:#334155; border-bottom:1px solid #f1f5f9; }
.co-doctable tr:last-child td{ border-bottom:none; }
.co-doctable tbody tr:hover{ background:#faf7ff; }
.co-alert{ background:#fef2f2; border:1px solid #fecaca; color:#b91c1c; border-radius:12px; padding:11px 14px; font-size:13px; margin-bottom:14px; }

/* custom dropdown */
.co-dd{ position:relative; }
.co-dd__btn{ appearance:none; display:inline-flex; align-items:center; gap:9px; width:100%; box-sizing:border-box;
  padding:10px 12px; border-radius:10px; border:1px solid #e2e8f0; background:#fff; color:#0f172a; font-size:13.5px;
  font-family:inherit; cursor:pointer; transition:border-color .15s, box-shadow .15s; }
.co-dd__btn:hover{ border-color:#cbd5e1; }
.co-dd__btn.open{ border-color:#7c3aed; box-shadow:0 0 0 3px rgba(124,58,237,.15); }
.co-dd__lbl{ flex:1; text-align:left; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.co-dd__lbl.ph{ color:#94a3b8; }
.co-dd__chev{ flex:0 0 auto; font-size:11px; color:#94a3b8; transition:transform .15s, color .15s; }
.co-dd__btn.open .co-dd__chev{ transform:rotate(180deg); color:#7c3aed; }
.co-dd__menu{ position:absolute; top:calc(100% + 6px); left:0; min-width:100%; z-index:300; box-sizing:border-box;
  background:#fff; border:1px solid #eef0f5; border-radius:12px; box-shadow:0 18px 44px -12px rgba(15,23,42,.3);
  padding:6px; max-height:288px; overflow-y:auto; animation:co-slide .16s ease; }
@keyframes co-slide{ from{opacity:0; transform:translateY(-4px);} to{opacity:1; transform:none;} }
.co-dd__opt{ display:flex; align-items:center; gap:10px; padding:9px 11px; border-radius:9px; font-size:13.5px;
  color:#334155; cursor:pointer; white-space:nowrap; transition:background .1s; }
.co-dd__opt:hover{ background:#f6f2ff; color:#1e293b; }
.co-dd__opt.sel{ color:#6d28d9; font-weight:600; background:#f6f2ff; }
.co-dd__dot{ width:8px; height:8px; border-radius:3px; background:#cbd5e1; flex:0 0 auto; transition:background .12s; }
.co-dd__opt:hover .co-dd__dot{ background:#a78bfa; }
.co-dd__opt.sel .co-dd__dot{ background:#7c3aed; }
.co-dd__check{ margin-left:auto; color:#7c3aed; font-size:13px; opacity:0; }
.co-dd__opt.sel .co-dd__check{ opacity:1; }

/* switch */
.co-switch{ position:relative; display:inline-flex; align-items:center; gap:10px; cursor:pointer; user-select:none;
  font-size:13px; font-weight:500; color:#334155; }
.co-switch input{ position:absolute; opacity:0; width:0; height:0; }
.co-switch__track{ width:42px; height:24px; border-radius:999px; background:#cbd5e1; position:relative;
  transition:background .18s, box-shadow .15s; flex:0 0 auto; }
.co-switch__track::after{ content:""; position:absolute; top:2px; left:2px; width:20px; height:20px; border-radius:50%;
  background:#fff; box-shadow:0 1px 3px rgba(15,23,42,.32); transition:transform .18s; }
.co-switch input:checked + .co-switch__track{ background:linear-gradient(135deg,#6d28d9,#7c3aed); }
.co-switch input:checked + .co-switch__track::after{ transform:translateX(18px); }
.co-switch input:focus-visible + .co-switch__track{ box-shadow:0 0 0 3px rgba(124,58,237,.28); }

.co-tplgrid{ display:grid; grid-template-columns:repeat(auto-fill,minmax(320px,1fr)); gap:14px; }
.co-tpl{ background:#fff; border:1px solid #eef0f5; border-radius:16px; padding:18px 18px 16px; display:flex; flex-direction:column;
  box-shadow:0 10px 30px -16px rgba(15,23,42,.16); transition:box-shadow .15s, transform .12s, border-color .15s; }
.co-tpl:hover{ box-shadow:0 16px 40px -16px rgba(109,40,217,.28); transform:translateY(-2px); border-color:#e6ddfb; }
.co-tpl__head{ display:flex; align-items:center; gap:11px; margin-bottom:10px; }
.co-tpl__ico{ width:40px; height:40px; border-radius:11px; display:flex; align-items:center; justify-content:center; font-size:20px;
  background:linear-gradient(135deg,#eef2ff,#f5f0ff); border:1px solid #eae7fb; flex:0 0 auto; }
.co-tpl__name{ font-weight:800; color:#1e293b; font-size:14.5px; line-height:1.2; }
.co-tpl__region{ display:inline-block; margin-top:3px; font-size:10px; font-weight:700; letter-spacing:.06em; text-transform:uppercase;
  color:#6d28d9; background:#f4f2fb; border:1px solid #ece7fa; padding:2px 8px; border-radius:6px; }
.co-tpl__desc{ font-size:12.5px; color:#64748b; line-height:1.5; flex:1; margin-bottom:12px; }
.co-tpl__meta{ display:flex; gap:8px; flex-wrap:wrap; margin-bottom:13px; }
.co-tpl__stat{ display:inline-flex; align-items:center; gap:5px; font-size:11px; font-weight:600; color:#5b21b6;
  background:#faf7ff; border:1px solid #f0eafe; padding:4px 9px; border-radius:7px; }
.co-tpl__actions{ display:flex; gap:8px; }
.co-preview{ background:#fff; border:1px solid #eef0f5; border-radius:16px; padding:18px 20px; margin-bottom:16px;
  box-shadow:0 12px 36px -14px rgba(15,23,42,.2); }
.co-preview__cols{ display:grid; grid-template-columns:1fr 1fr 1fr; gap:22px; }
@media (max-width:820px){ .co-preview__cols{ grid-template-columns:1fr; } }
.co-preview h4{ margin:0 0 10px; font-size:11px; font-weight:800; color:#6d28d9; text-transform:uppercase; letter-spacing:.08em; }
.co-tree__row{ display:flex; align-items:center; gap:7px; padding:3px 0; font-size:13px; color:#334155; }
.co-tree__sw{ width:10px; height:10px; border-radius:3px; flex:0 0 auto; }

.co-danger-card{ background:#fff; border:1px solid #fee2e2; border-radius:16px; padding:18px 20px; margin-top:22px;
  box-shadow:0 10px 30px -18px rgba(185,28,28,.25); }
.co-danger-card__title{ font-size:11px; font-weight:800; color:#b91c1c; text-transform:uppercase; letter-spacing:.09em; margin-bottom:10px; display:flex; align-items:center; gap:8px; }
.co-danger{ appearance:none; background:linear-gradient(135deg,#dc2626,#b91c1c); color:#fff; border:none; font-weight:700; font-size:13.5px;
  padding:11px 20px; border-radius:12px; cursor:pointer; box-shadow:0 8px 20px -4px rgba(220,38,38,.4); transition:filter .15s, transform .12s; }
.co-danger:hover{ filter:brightness(1.07); } .co-danger:active{ transform:translateY(1px); }
.co-danger:disabled{ opacity:.5; cursor:not-allowed; filter:none; box-shadow:none; }
.co-measure{ display:flex; gap:8px; flex-wrap:wrap; margin:2px 0 14px; }
.co-measure__item{ background:#f8fafc; border:1px solid #eef0f5; border-radius:11px; padding:9px 14px; min-width:74px; }
.co-measure__item b{ font-size:19px; font-weight:800; color:#0f172a; display:block; line-height:1; }
.co-measure__item span{ font-size:11px; color:#64748b; text-transform:uppercase; letter-spacing:.04em; }
.co-modal__backdrop{ position:fixed; inset:0; background:rgba(15,23,42,.55); backdrop-filter:blur(2px); z-index:600;
  display:flex; align-items:center; justify-content:center; padding:20px; }
.co-modal{ background:#fff; border-radius:18px; max-width:540px; width:100%; padding:24px 26px; box-shadow:0 30px 80px -20px rgba(15,23,42,.5); animation:co-slide .18s ease; }
.co-modal__title{ font-size:17px; font-weight:800; color:#b91c1c; display:flex; align-items:center; gap:9px; margin:0 0 6px; }
.co-modal__sub{ font-size:13px; color:#64748b; margin:0 0 14px; line-height:1.55; }
.co-check{ display:flex; align-items:center; gap:9px; font-size:13.5px; color:#334155; margin:7px 0; cursor:pointer; }
.co-check input{ width:16px; height:16px; accent-color:#b91c1c; }
.co-modal__row{ display:flex; gap:10px; justify-content:flex-end; margin-top:18px; }
`;

function Dropdown({ value, onChange, options, placeholder }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const sel = options.find((o) => String(o.value) === String(value));
  return (
    <div className="co-dd" ref={ref}>
      <button type="button" className={`co-dd__btn${open ? " open" : ""}`} onClick={() => setOpen((o) => !o)}>
        <span className={`co-dd__lbl${sel ? "" : " ph"}`}>{sel ? sel.label : placeholder}</span>
        <span className="co-dd__chev">▾</span>
      </button>
      {open && (
        <div className="co-dd__menu">
          {options.map((o) => {
            const on = String(o.value) === String(value);
            return (
              <div key={o.value} className={`co-dd__opt${on ? " sel" : ""}`}
                onClick={() => { onChange(o.value); setOpen(false); }}>
                <span className="co-dd__dot" />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{o.label}</span>
                <span className="co-dd__check">✓</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Switch({ checked, onChange, label }) {
  return (
    <label className="co-switch">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="co-switch__track" />
      {label}
    </label>
  );
}

export default function ContentOrganizationPage() {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const locale = (i18n.language || "en").split("-")[0];

  const [tab, setTab] = useState("folders");
  const [databases, setDatabases] = useState([]);
  const [err, setErr] = useState(null);
  const [counts, setCounts] = useState({ folders: 0, taxonomy: 0, types: 0 });
  const setCount = useCallback((k, n) => setCounts((c) => ({ ...c, [k]: n })), []);

  useEffect(() => {
    getDatabases(token)
      .then((d) => setDatabases(Array.isArray(d) ? d : (d?.databases ?? d?.Databases ?? [])))
      .catch(() => {});
  }, [token]);

  const tabs = [
    { value: "folders", label: t("contentOrg.tabFolders", { defaultValue: "Folders" }) },
    { value: "taxonomy", label: t("contentOrg.tabTaxonomy", { defaultValue: "Taxonomy" }) },
    { value: "types", label: t("contentOrg.tabTypes", { defaultValue: "Content Types" }) },
    { value: "templates", label: t("contentOrg.tabTemplates", { defaultValue: "Templates" }) },
    { value: "starter", label: t("contentOrg.tabStarter", { defaultValue: "Starter workspace" }) },
    { value: "assign", label: t("contentOrg.tabAssign", { defaultValue: "Assign Document" }) },
  ];
  const statLabel = tab === "taxonomy" ? t("contentOrg.terms", { defaultValue: "Taxonomy terms" })
    : tab === "types" ? t("contentOrg.tabTypes", { defaultValue: "Content Types" })
    : t("contentOrg.tabFolders", { defaultValue: "Folders" });
  const statValue = tab === "taxonomy" ? counts.taxonomy : tab === "types" ? counts.types : counts.folders;

  return (
    <>
      <style>{CO_STYLES}</style>
      <HeaderBar />
      <NavBar />
      <div style={{ maxWidth: 1080, margin: "0 auto", padding: "18px 16px 64px" }}>
        <div className="co-hero">
          <div className="co-hero__glow" />
          <div className="co-hero__logo"><FolderTreeArt /></div>
          <div className="co-hero__body">
            <h1 className="co-hero__title">{t("contentOrg.title", { defaultValue: "Content Organization" })}</h1>
            <p className="co-hero__sub">{t("contentOrg.subtitle", { defaultValue: "Folders, taxonomy terms and content types for the archive." })}</p>
          </div>
          {["folders", "taxonomy", "types"].includes(tab) && (
            <div className="co-hero__stat">
              <b>{statValue}</b>
              <span><i className="co-hero__dot" /> {statLabel}</span>
            </div>
          )}
        </div>

        <div className="co-tabs">
          {tabs.map((tb) => (
            <button key={tb.value} className={tab === tb.value ? "on" : ""} onClick={() => setTab(tb.value)}>{tb.label}</button>
          ))}
        </div>

        {err && <div className="co-alert">{err}</div>}

        {tab === "folders" && <FoldersTab token={token} databases={databases} onError={setErr} onCount={(n) => setCount("folders", n)} t={t} />}
        {tab === "taxonomy" && <TaxonomyTab token={token} onError={setErr} onCount={(n) => setCount("taxonomy", n)} t={t} />}
        {tab === "types" && <TypesTab token={token} databases={databases} onError={setErr} onCount={(n) => setCount("types", n)} t={t} />}
        {tab === "templates" && <TemplatesTab token={token} locale={locale} onError={setErr} t={t} />}
        {tab === "starter" && <StarterWorkspaceTab token={token} onError={setErr} t={t} />}
        {tab === "assign" && <AssignTab token={token} databases={databases} onError={setErr} t={t} />}

        <ClearPanel token={token} onError={setErr} t={t} />
      </div>
    </>
  );
}

function nodeOptions(nodes, t, { rootLabel } = {}) {
  const opts = [{ value: "", label: rootLabel ?? t("contentOrg.root", { defaultValue: "(root)" }) }];
  nodes.forEach((n) => opts.push({ value: String(n.id), label: `${"— ".repeat(n.depth)}${n.name}` }));
  return opts;
}

function TreeNode({ node, icon, children }) {
  return (
    <div className={`co-node${node.isActive ? "" : " arch"}`}>
      {node.depth > 0 && <span className="co-node__rail" style={{ marginLeft: (node.depth - 1) * 18 + 6, marginRight: 4 }} />}
      <span className="co-node__ico">{icon}</span>
      <span className="co-node__name">{node.name}</span>
      <span className="co-node__path">{node.path}</span>
      {!node.isActive && <span className="co-status co-status--off" style={{ marginLeft: 8 }}>archived</span>}
      <div className="co-node__actions">{children}</div>
    </div>
  );
}

// ─────────────────────────── Folders ───────────────────────────

function FoldersTab({ token, databases, onError, onCount, t }) {
  const { hasFeature } = useLicense();
  const canBrowse = hasFeature("content-governance"); // folder→documents browse is a content-governance feature
  const [nodes, setNodes] = useState([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [form, setForm] = useState({ id: null, parentId: "", name: "", description: "" });
  const [busy, setBusy] = useState(false);
  const [browse, setBrowse] = useState(null); // { folder } being browsed, or null

  const load = useCallback(() => {
    getFolders(token, { includeInactive })
      .then((d) => { const l = Array.isArray(d) ? d : []; setNodes(l); onCount(l.filter((x) => x.isActive).length); })
      .catch(() => onError(t("contentOrg.loadFailed", { defaultValue: "Failed to load." })));
  }, [token, includeInactive, onError, onCount, t]);
  useEffect(() => { load(); }, [load]);

  const reset = () => setForm({ id: null, parentId: "", name: "", description: "" });
  const save = async () => {
    if (!form.name.trim()) return;
    setBusy(true); onError(null);
    try {
      const body = { parentId: form.parentId ? Number(form.parentId) : null, name: form.name.trim(), description: form.description || null };
      if (form.id) await updateFolder(token, form.id, body); else await createFolder(token, body);
      reset(); load();
    } catch (e) { onError(e?.message || t("contentOrg.saveFailed", { defaultValue: "Save failed." })); }
    finally { setBusy(false); }
  };
  const doMove = async (id, newParentId) => {
    onError(null);
    try { await moveFolder(token, id, { newParentId: newParentId ? Number(newParentId) : null }); load(); }
    catch (e) { onError(e?.message || t("contentOrg.saveFailed", { defaultValue: "Save failed." })); }
  };
  const toggleArchive = async (n) => {
    onError(null);
    try { n.isActive ? await archiveFolder(token, n.id) : await restoreFolder(token, n.id); load(); }
    catch (e) { onError(e?.message || t("contentOrg.saveFailed", { defaultValue: "Save failed." })); }
  };

  return (
    <>
      <div className="co-card">
        <div className="co-card__title">🗂️ {form.id ? t("contentOrg.editFolder", { defaultValue: "Edit folder" }) : t("contentOrg.newFolder", { defaultValue: "New folder" })}</div>
        <div className="co-grid" style={{ gridTemplateColumns: "1.4fr 1fr 1.4fr auto" }}>
          <div className="co-field">
            <label className="co-lbl">{t("contentOrg.name", { defaultValue: "Name" })}</label>
            <input className="co-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} onKeyDown={(e) => e.key === "Enter" && save()} />
          </div>
          <div className="co-field">
            <label className="co-lbl">{t("contentOrg.parent", { defaultValue: "Parent" })}</label>
            <Dropdown value={form.parentId} onChange={(v) => setForm({ ...form, parentId: v })}
              options={nodeOptions(nodes.filter((n) => n.id !== form.id), t)} placeholder={t("contentOrg.root", { defaultValue: "(root)" })} />
          </div>
          <div className="co-field">
            <label className="co-lbl">{t("contentOrg.description", { defaultValue: "Description" })}</label>
            <input className="co-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="co-cta" disabled={busy || !form.name.trim()} onClick={save}>{form.id ? t("contentOrg.save", { defaultValue: "Save" }) : t("contentOrg.create", { defaultValue: "Create" })}</button>
            {form.id && <button className="co-ghost" onClick={reset}>{t("contentOrg.cancel", { defaultValue: "Cancel" })}</button>}
          </div>
        </div>
      </div>

      <FolderBackfillPanel token={token} databases={databases} folders={nodes} onError={onError} t={t} />

      <div className="co-toolbar">
        <Switch checked={includeInactive} onChange={setIncludeInactive} label={t("contentOrg.showArchived", { defaultValue: "Show archived" })} />
        <span className="co-count">{nodes.length}</span>
      </div>

      <div className="co-tablecard">
        {nodes.length === 0
          ? <div className="co-empty"><div className="co-empty__ring">🗂️</div><div className="co-empty__title">{t("contentOrg.noFolders", { defaultValue: "No folders yet" })}</div><div className="co-empty__hint">{t("contentOrg.noFoldersHint", { defaultValue: "Create your first folder above." })}</div></div>
          : nodes.map((n) => (
            <TreeNode key={n.id} node={n} icon="📁">
              <button className="co-btn" onClick={() => setForm({ id: n.id, parentId: n.parentId ? String(n.parentId) : "", name: n.name, description: n.description || "" })}>{t("contentOrg.edit", { defaultValue: "Edit" })}</button>
              <div style={{ width: 160 }}>
                <Dropdown value={n.parentId ? String(n.parentId) : ""} onChange={(v) => doMove(n.id, v)}
                  options={nodeOptions(nodes.filter((x) => x.id !== n.id), t, { rootLabel: t("contentOrg.moveToRoot", { defaultValue: "Move to root" }) })}
                  placeholder={t("contentOrg.move", { defaultValue: "Move…" })} />
              </div>
              <button className="co-btn" onClick={() => toggleArchive(n)}>{n.isActive ? t("contentOrg.archive", { defaultValue: "Archive" }) : t("contentOrg.restore", { defaultValue: "Restore" })}</button>
              {canBrowse && <button className="co-btn" onClick={() => setBrowse({ folder: n })}>📄 {t("contentOrg.documents", { defaultValue: "Documents" })}</button>}
            </TreeNode>
          ))}
      </div>

      {canBrowse && browse && (
        <FolderDocumentsPanel token={token} folder={browse.folder} onClose={() => setBrowse(null)} onError={onError} t={t} />
      )}
    </>
  );
}

// Browse the documents filed into a folder (subtree-aware, across the org's databases).
// Gated: only rendered when the content-governance license feature is enabled.
function FolderDocumentsPanel({ token, folder, onClose, onError, t }) {
  const [includeSubtree, setIncludeSubtree] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true); onError(null);
    getFolderDocuments(token, folder.id, { includeSubtree, max: 200 })
      .then((d) => setData(d))
      .catch((e) => onError(e?.message || t("contentOrg.docsFailed", { defaultValue: "Failed to load documents." })))
      .finally(() => setLoading(false));
  }, [token, folder.id, includeSubtree, onError, t]);
  useEffect(() => { load(); }, [load]);

  const docs = data?.documents ?? [];
  const fmtDate = (v) => { if (!v) return "—"; const s = String(v); return s.length >= 10 ? s.slice(0, 10) : s; };

  return (
    <div className="co-card">
      <div className="co-card__title" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span>📄 {t("contentOrg.docsIn", { defaultValue: "Documents in" })} <b style={{ color: "#0f172a", textTransform: "none", letterSpacing: 0 }}>{folder.path || folder.name}</b></span>
        <button className="co-ghost" onClick={onClose}>{t("contentOrg.close", { defaultValue: "Close" })}</button>
      </div>
      <div style={{ display: "flex", gap: 14, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
        <Switch checked={includeSubtree} onChange={setIncludeSubtree} label={t("contentOrg.includeSubfolders", { defaultValue: "Include subfolders" })} />
        {data && (
          <span className="co-status co-status--on">
            <i className="co-status__dot" />{data.count} {t("contentOrg.docsFound", { defaultValue: "documents" })}
            {data.subtreeFolders > 1 && ` · ${data.subtreeFolders} ${t("contentOrg.foldersScanned", { defaultValue: "folders" })}`}
            {data.truncated && ` · ${t("contentOrg.docsTruncated", { defaultValue: "showing first 200" })}`}
          </span>
        )}
      </div>
      <div className="co-tablecard">
        {loading ? (
          <div className="co-empty"><div className="co-empty__title">{t("contentOrg.loading", { defaultValue: "Loading…" })}</div></div>
        ) : docs.length === 0 ? (
          <div className="co-empty"><div className="co-empty__ring">📄</div><div className="co-empty__title">{t("contentOrg.noDocs", { defaultValue: "No documents filed here" })}</div><div className="co-empty__hint">{t("contentOrg.noDocsHint", { defaultValue: "File documents via ingest, the Assign Document tab, or Run backfill." })}</div></div>
        ) : (
          <table className="co-doctable">
            <thead>
              <tr>
                <th>{t("contentOrg.docDatabase", { defaultValue: "Database" })}</th>
                <th>{t("contentOrg.docArchiveNo", { defaultValue: "Archive #" })}</th>
                <th>{t("contentOrg.docFileName", { defaultValue: "File name" })}</th>
                <th>{t("contentOrg.docDate", { defaultValue: "Document date" })}</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d, i) => (
                <tr key={`${d.dbName}-${d.id}-${i}`}>
                  <td>{d.dbName}</td>
                  <td>{d.archiveNo ?? d.id}</td>
                  <td>{d.fileName ?? "—"}</td>
                  <td>{fmtDate(d.documentDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function FolderBackfillPanel({ token, databases, folders, onError, t }) {
  const [dbSel, setDbSel] = useState("");
  const [folderSel, setFolderSel] = useState("");
  const [onlyUnfoldered, setOnlyUnfoldered] = useState(true);
  const [job, setJob] = useState(null);
  const timer = useRef(null);

  const poll = useCallback((databaseId) => {
    getFolderBackfillStatus(token, databaseId).then((j) => {
      setJob(j);
      if (j && j.state === "Running") timer.current = setTimeout(() => poll(databaseId), 2000);
    }).catch(() => {});
  }, [token]);
  useEffect(() => () => clearTimeout(timer.current), []);

  const run = async () => {
    if (!dbSel) return;
    onError(null);
    try {
      const j = await triggerFolderBackfill(token, { databaseId: Number(dbSel), targetFolderId: folderSel ? Number(folderSel) : null, onlyUnfoldered });
      setJob(j); poll(Number(dbSel));
    } catch (e) { onError(e?.message || t("contentOrg.backfillFailed", { defaultValue: "Backfill failed." })); }
  };

  return (
    <div className="co-card">
      <div className="co-card__title">♻️ {t("contentOrg.backfill", { defaultValue: "Backfill folder onto existing documents" })}</div>
      <div className="co-grid" style={{ gridTemplateColumns: "1fr 1.4fr" }}>
        <div className="co-field">
          <label className="co-lbl">{t("contentOrg.database", { defaultValue: "Database" })}</label>
          <Dropdown value={dbSel} onChange={setDbSel} placeholder={t("contentOrg.selectDb", { defaultValue: "Select database" })}
            options={databases.map((d) => ({ value: String(dbId(d)), label: dbName(d) }))} />
        </div>
        <div className="co-field">
          <label className="co-lbl">{t("contentOrg.targetFolder", { defaultValue: "Target folder (blank = content-type default)" })}</label>
          <Dropdown value={folderSel} onChange={setFolderSel} placeholder={t("contentOrg.contentTypeDefault", { defaultValue: "Content-type default" })}
            options={nodeOptions(folders, t, { rootLabel: t("contentOrg.contentTypeDefault", { defaultValue: "Content-type default" }) })} />
        </div>
      </div>
      <div style={{ display: "flex", gap: 14, alignItems: "center", marginTop: 14, flexWrap: "wrap" }}>
        <Switch checked={onlyUnfoldered} onChange={setOnlyUnfoldered} label={t("contentOrg.onlyUnfoldered", { defaultValue: "Only documents without a folder" })} />
        <button className="co-cta" disabled={!dbSel || (job && job.state === "Running")} onClick={run}>{t("contentOrg.runBackfill", { defaultValue: "Run backfill" })}</button>
        {job && job.state === "Running" && <button className="co-ghost" onClick={() => cancelFolderBackfill(token, Number(dbSel))}>{t("contentOrg.cancel", { defaultValue: "Cancel" })}</button>}
        {job && (
          <span className={`co-status co-status--${job.state === "Running" ? "warn" : job.state === "Failed" ? "err" : "on"}`}>
            <i className="co-status__dot" />{job.state} · {t("contentOrg.scanned", { defaultValue: "scanned" })} {job.scanned} · {t("contentOrg.assigned", { defaultValue: "assigned" })} {job.assigned}
          </span>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────── Taxonomy ───────────────────────────

function TaxonomyTab({ token, onError, onCount, t }) {
  const [nodes, setNodes] = useState([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [form, setForm] = useState({ id: null, parentId: "", name: "", description: "", color: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    getTaxonomyNodes(token, { includeInactive })
      .then((d) => { const l = Array.isArray(d) ? d : []; setNodes(l); onCount(l.filter((x) => x.isActive).length); })
      .catch(() => onError(t("contentOrg.loadFailed", { defaultValue: "Failed to load." })));
  }, [token, includeInactive, onError, onCount, t]);
  useEffect(() => { load(); }, [load]);

  const reset = () => setForm({ id: null, parentId: "", name: "", description: "", color: "" });
  const save = async () => {
    if (!form.name.trim()) return;
    setBusy(true); onError(null);
    try {
      const body = { parentId: form.parentId ? Number(form.parentId) : null, name: form.name.trim(), description: form.description || null, color: form.color || null };
      if (form.id) await updateTaxonomyNode(token, form.id, body); else await createTaxonomyNode(token, body);
      reset(); load();
    } catch (e) { onError(e?.message || t("contentOrg.saveFailed", { defaultValue: "Save failed." })); }
    finally { setBusy(false); }
  };
  const doMove = async (id, newParentId) => {
    onError(null);
    try { await moveTaxonomyNode(token, id, { newParentId: newParentId ? Number(newParentId) : null }); load(); }
    catch (e) { onError(e?.message || t("contentOrg.saveFailed", { defaultValue: "Save failed." })); }
  };
  const toggleArchive = async (n) => {
    onError(null);
    try { n.isActive ? await archiveTaxonomyNode(token, n.id) : await restoreTaxonomyNode(token, n.id); load(); }
    catch (e) { onError(e?.message || t("contentOrg.saveFailed", { defaultValue: "Save failed." })); }
  };

  return (
    <>
      <div className="co-card">
        <div className="co-card__title">🏷️ {form.id ? t("contentOrg.editTerm", { defaultValue: "Edit term" }) : t("contentOrg.newTerm", { defaultValue: "New term" })}</div>
        <div className="co-grid" style={{ gridTemplateColumns: "1.4fr 1fr auto 1.4fr auto" }}>
          <div className="co-field">
            <label className="co-lbl">{t("contentOrg.name", { defaultValue: "Name" })}</label>
            <input className="co-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} onKeyDown={(e) => e.key === "Enter" && save()} />
          </div>
          <div className="co-field">
            <label className="co-lbl">{t("contentOrg.parent", { defaultValue: "Parent" })}</label>
            <Dropdown value={form.parentId} onChange={(v) => setForm({ ...form, parentId: v })}
              options={nodeOptions(nodes.filter((n) => n.id !== form.id), t)} placeholder={t("contentOrg.root", { defaultValue: "(root)" })} />
          </div>
          <div className="co-field">
            <label className="co-lbl">{t("contentOrg.color", { defaultValue: "Color" })}</label>
            <input type="color" className="co-input co-color" value={form.color || "#6d28d9"} onChange={(e) => setForm({ ...form, color: e.target.value })} />
          </div>
          <div className="co-field">
            <label className="co-lbl">{t("contentOrg.description", { defaultValue: "Description" })}</label>
            <input className="co-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="co-cta" disabled={busy || !form.name.trim()} onClick={save}>{form.id ? t("contentOrg.save", { defaultValue: "Save" }) : t("contentOrg.create", { defaultValue: "Create" })}</button>
            {form.id && <button className="co-ghost" onClick={reset}>{t("contentOrg.cancel", { defaultValue: "Cancel" })}</button>}
          </div>
        </div>
      </div>

      <div className="co-toolbar">
        <Switch checked={includeInactive} onChange={setIncludeInactive} label={t("contentOrg.showArchived", { defaultValue: "Show archived" })} />
        <span className="co-count">{nodes.length}</span>
      </div>

      <div className="co-tablecard">
        {nodes.length === 0
          ? <div className="co-empty"><div className="co-empty__ring">🏷️</div><div className="co-empty__title">{t("contentOrg.noTerms", { defaultValue: "No terms yet" })}</div><div className="co-empty__hint">{t("contentOrg.noTermsHint", { defaultValue: "Create your first term above." })}</div></div>
          : nodes.map((n) => (
            <TreeNode key={n.id} node={n} icon="🏷️">
              {n.color && <span className="co-swatch" style={{ background: n.color }} />}
              <button className="co-btn" onClick={() => setForm({ id: n.id, parentId: n.parentId ? String(n.parentId) : "", name: n.name, description: n.description || "", color: n.color || "" })}>{t("contentOrg.edit", { defaultValue: "Edit" })}</button>
              <div style={{ width: 160 }}>
                <Dropdown value={n.parentId ? String(n.parentId) : ""} onChange={(v) => doMove(n.id, v)}
                  options={nodeOptions(nodes.filter((x) => x.id !== n.id), t, { rootLabel: t("contentOrg.moveToRoot", { defaultValue: "Move to root" }) })}
                  placeholder={t("contentOrg.move", { defaultValue: "Move…" })} />
              </div>
              <button className="co-btn" onClick={() => toggleArchive(n)}>{n.isActive ? t("contentOrg.archive", { defaultValue: "Archive" }) : t("contentOrg.restore", { defaultValue: "Restore" })}</button>
            </TreeNode>
          ))}
      </div>
    </>
  );
}

// ─────────────────────────── Starter workspace ───────────────────────────
//
// The folder tree every NEW user is handed the first time they open their workspace. An organization
// that never touches this screen still works: with no rows here, provisioning uses the built-in
// default set, translated into the signing-in user's own language.
//
// Editing is NOT retroactive, and the screen says so. Once somebody has a workspace it is theirs —
// rewriting it from an admin screen would move documents they had already filed.

function StarterWorkspaceTab({ token, onError, t }) {
  const { i18n } = useTranslation();
  const [state, setState] = useState({ folders: [], usingDefault: true, defaultFolders: [], provisionedUsers: 0 });
  const [form, setForm] = useState({ id: null, parentId: "", name: "", description: "" });
  const [busy, setBusy] = useState(false);

  // The built-in preview is named in the admin's own language, since that is what a user in that language
  // is actually provisioned with — so a language switch has to re-read it.
  const lang = i18n?.language;
  const load = useCallback(() => {
    getWorkspaceTemplate(token, { locale: lang })
      .then((d) => setState(d || { folders: [] }))
      .catch(() => onError(t("contentOrg.loadFailed", { defaultValue: "Failed to load." })));
  }, [token, onError, t, lang]);
  useEffect(() => { load(); }, [load]);

  const nodes = state.folders || [];
  const reset = () => setForm({ id: null, parentId: "", name: "", description: "" });

  const save = async () => {
    if (!form.name.trim()) return;
    setBusy(true); onError(null);
    try {
      const body = { parentId: form.parentId ? Number(form.parentId) : null, name: form.name.trim(), description: form.description || null };
      if (form.id) await updateWorkspaceTemplateFolder(token, form.id, body);
      else await createWorkspaceTemplateFolder(token, body);
      reset(); load();
    } catch (e) { onError(e?.message || t("contentOrg.saveFailed", { defaultValue: "Save failed." })); }
    finally { setBusy(false); }
  };

  const seed = async () => {
    setBusy(true); onError(null);
    try { await seedWorkspaceTemplate(token, lang); load(); }
    catch (e) { onError(e?.message || t("contentOrg.saveFailed", { defaultValue: "Save failed." })); }
    finally { setBusy(false); }
  };

  const remove = async (n) => {
    onError(null);
    try { await archiveWorkspaceTemplateFolder(token, n.id); load(); }
    catch (e) { onError(e?.message || t("contentOrg.saveFailed", { defaultValue: "Save failed." })); }
  };

  return (
    <>
      <div className="co-card">
        <div className="co-card__title">
          👤 {t("contentOrg.starterTitle", { defaultValue: "Starter workspace for new users" })}
        </div>
        <p className="co-hint" style={{ margin: "0 0 12px" }}>
          {state.usingDefault
            ? t("contentOrg.starterUsingDefault", {
                defaultValue:
                  "New users currently get the built-in starter folders ({{names}}), translated into their own language. Add folders below to use your own tree instead.",
                names: (state.defaultFolders || []).join(", "),
              })
            : t("contentOrg.starterUsingCustom", {
                defaultValue: "New users get the tree below. Changes apply to users provisioned from now on; existing workspaces are never rewritten.",
              })}
        </p>

        <div className="co-grid" style={{ gridTemplateColumns: "1.4fr 1fr 1.4fr auto" }}>
          <div className="co-field">
            <label className="co-lbl">{t("contentOrg.name", { defaultValue: "Name" })}</label>
            <input className="co-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} onKeyDown={(e) => e.key === "Enter" && save()} />
          </div>
          <div className="co-field">
            <label className="co-lbl">{t("contentOrg.parent", { defaultValue: "Parent" })}</label>
            <Dropdown value={form.parentId} onChange={(v) => setForm({ ...form, parentId: v })}
              options={nodeOptions(nodes.filter((n) => n.id !== form.id), t)} placeholder={t("contentOrg.root", { defaultValue: "(root)" })} />
          </div>
          <div className="co-field">
            <label className="co-lbl">{t("contentOrg.description", { defaultValue: "Description" })}</label>
            <input className="co-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
            <button className="co-cta" disabled={busy || !form.name.trim()} onClick={save}>
              {form.id ? t("contentOrg.save", { defaultValue: "Save" }) : t("contentOrg.create", { defaultValue: "Create" })}
            </button>
            {form.id && <button className="co-ghost" onClick={reset}>{t("contentOrg.cancel", { defaultValue: "Cancel" })}</button>}
          </div>
        </div>
      </div>

      <div className="co-toolbar">
        <span className="co-count">{nodes.length}</span>
        {nodes.length === 0 && (
          <button className="co-btn" disabled={busy} onClick={seed}>
            {t("contentOrg.starterSeed", { defaultValue: "Start from the built-in set" })}
          </button>
        )}
        <span className="co-hint" style={{ marginLeft: "auto" }}>
          {t("contentOrg.starterProvisioned", { defaultValue: "{{count}} workspace(s) already set up", count: state.provisionedUsers || 0 })}
        </span>
      </div>

      <div className="co-tablecard">
        {nodes.length === 0
          ? <div className="co-empty">
              <div className="co-empty__ring">👤</div>
              <div className="co-empty__title">{t("contentOrg.starterEmpty", { defaultValue: "Using the built-in starter folders" })}</div>
              <div className="co-empty__hint">{t("contentOrg.starterEmptyHint", { defaultValue: "Add a folder above, or start from the built-in set and edit it." })}</div>
            </div>
          : nodes.map((n) => (
            <TreeNode key={n.id} node={n} icon="📁">
              <button className="co-btn" onClick={() => setForm({ id: n.id, parentId: n.parentId ? String(n.parentId) : "", name: n.name, description: n.description || "" })}>
                {t("contentOrg.edit", { defaultValue: "Edit" })}
              </button>
              <button className="co-btn" onClick={() => remove(n)}>{t("contentOrg.archive", { defaultValue: "Archive" })}</button>
            </TreeNode>
          ))}
      </div>
    </>
  );
}

// ─────────────────────────── Content Types ───────────────────────────

function TypesTab({ token, databases, onError, onCount, t }) {
  const emptyType = () => ({ id: null, name: "", description: "", defaultFolderId: "", defaultClassification: "", defaultRetentionDays: "", icon: "", color: "" });
  const [types, setTypes] = useState([]);
  const [folders, setFolders] = useState([]);
  const [form, setForm] = useState(emptyType());
  const [busy, setBusy] = useState(false);
  const [linkDb, setLinkDb] = useState("");
  const [linkType, setLinkType] = useState("");

  const load = useCallback(() => {
    getContentTypes(token).then((d) => { const l = Array.isArray(d) ? d : []; setTypes(l); onCount(l.length); }).catch(() => onError(t("contentOrg.loadFailed", { defaultValue: "Failed to load." })));
    getFolders(token, {}).then((d) => setFolders(Array.isArray(d) ? d : [])).catch(() => {});
  }, [token, onError, onCount, t]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name.trim()) return;
    setBusy(true); onError(null);
    try {
      const body = {
        name: form.name.trim(), description: form.description || null,
        defaultFolderId: form.defaultFolderId ? Number(form.defaultFolderId) : null,
        defaultClassification: form.defaultClassification || null,
        defaultRetentionDays: form.defaultRetentionDays ? Number(form.defaultRetentionDays) : null,
        icon: form.icon || null, color: form.color || null,
      };
      if (form.id) await updateContentType(token, form.id, body); else await createContentType(token, body);
      setForm(emptyType()); load();
    } catch (e) { onError(e?.message || t("contentOrg.saveFailed", { defaultValue: "Save failed." })); }
    finally { setBusy(false); }
  };
  const saveLink = async () => {
    if (!linkDb) return;
    onError(null);
    try { await setBaseContentType(token, Number(linkDb), { contentTypeId: linkType ? Number(linkType) : null }); }
    catch (e) { onError(e?.message || t("contentOrg.saveFailed", { defaultValue: "Save failed." })); }
  };
  useEffect(() => {
    if (!linkDb) { setLinkType(""); return; }
    getBaseContentType(token, Number(linkDb)).then((r) => setLinkType(r?.contentTypeId ? String(r.contentTypeId) : "")).catch(() => {});
  }, [token, linkDb]);

  return (
    <>
      <div className="co-card">
        <div className="co-card__title">📁 {form.id ? t("contentOrg.editType", { defaultValue: "Edit content type" }) : t("contentOrg.newType", { defaultValue: "New content type" })}</div>
        <div className="co-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
          <div className="co-field">
            <label className="co-lbl">{t("contentOrg.name", { defaultValue: "Name" })}</label>
            <input className="co-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="co-field">
            <label className="co-lbl">{t("contentOrg.defaultFolder", { defaultValue: "Default folder" })}</label>
            <Dropdown value={form.defaultFolderId} onChange={(v) => setForm({ ...form, defaultFolderId: v })}
              options={nodeOptions(folders, t, { rootLabel: t("contentOrg.none", { defaultValue: "(none)" }) })} placeholder={t("contentOrg.none", { defaultValue: "(none)" })} />
          </div>
          <div className="co-field">
            <label className="co-lbl">{t("contentOrg.defaultClassification", { defaultValue: "Default classification" })}</label>
            <input className="co-input" value={form.defaultClassification} onChange={(e) => setForm({ ...form, defaultClassification: e.target.value })} />
          </div>
          <div className="co-field">
            <label className="co-lbl">{t("contentOrg.retentionDays", { defaultValue: "Default retention (days)" })}</label>
            <input className="co-input" type="number" value={form.defaultRetentionDays} onChange={(e) => setForm({ ...form, defaultRetentionDays: e.target.value })} />
          </div>
          <div className="co-field" style={{ gridColumn: "1 / -1" }}>
            <label className="co-lbl">{t("contentOrg.description", { defaultValue: "Description" })}</label>
            <input className="co-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button className="co-cta" disabled={busy || !form.name.trim()} onClick={save}>{form.id ? t("contentOrg.save", { defaultValue: "Save" }) : t("contentOrg.create", { defaultValue: "Create" })}</button>
          {form.id && <button className="co-ghost" onClick={() => setForm(emptyType())}>{t("contentOrg.cancel", { defaultValue: "Cancel" })}</button>}
        </div>
      </div>

      <div className="co-card">
        <div className="co-card__title">🔗 {t("contentOrg.linkBase", { defaultValue: "Assign a content type to a document base" })}</div>
        <div className="co-grid" style={{ gridTemplateColumns: "1fr 1fr auto" }}>
          <div className="co-field">
            <label className="co-lbl">{t("contentOrg.database", { defaultValue: "Database" })}</label>
            <Dropdown value={linkDb} onChange={setLinkDb} placeholder={t("contentOrg.selectDb", { defaultValue: "Select database" })}
              options={databases.map((d) => ({ value: String(dbId(d)), label: dbName(d) }))} />
          </div>
          <div className="co-field">
            <label className="co-lbl">{t("contentOrg.contentType", { defaultValue: "Content type" })}</label>
            <Dropdown value={linkType} onChange={setLinkType} placeholder={t("contentOrg.none", { defaultValue: "(none)" })}
              options={[{ value: "", label: t("contentOrg.none", { defaultValue: "(none)" }) }, ...types.map((c) => ({ value: String(c.id), label: c.name }))]} />
          </div>
          <div style={{ display: "flex", alignItems: "end" }}><button className="co-cta" disabled={!linkDb} onClick={saveLink}>{t("contentOrg.save", { defaultValue: "Save" })}</button></div>
        </div>
      </div>

      <div className="co-tablecard">
        {types.length === 0
          ? <div className="co-empty"><div className="co-empty__ring">📁</div><div className="co-empty__title">{t("contentOrg.noTypes", { defaultValue: "No content types yet" })}</div><div className="co-empty__hint">{t("contentOrg.noTypesHint", { defaultValue: "Create your first content type above." })}</div></div>
          : types.map((c) => (
            <div key={c.id} className="co-node">
              <span className="co-node__ico">{c.icon || "📁"}</span>
              <span className="co-node__name">{c.name}</span>
              {c.defaultClassification && <span className="co-chip" style={{ marginLeft: 8 }}>{c.defaultClassification}</span>}
              {c.description && <span className="co-node__path">{c.description}</span>}
              <div className="co-node__actions">
                <button className="co-btn" onClick={() => setForm({ id: c.id, name: c.name, description: c.description || "", defaultFolderId: c.defaultFolderId ? String(c.defaultFolderId) : "", defaultClassification: c.defaultClassification || "", defaultRetentionDays: c.defaultRetentionDays ? String(c.defaultRetentionDays) : "", icon: c.icon || "", color: c.color || "" })}>{t("contentOrg.edit", { defaultValue: "Edit" })}</button>
                <button className="co-btn" onClick={async () => { try { await archiveContentType(token, c.id); load(); } catch (e) { onError(e?.message); } }}>{t("contentOrg.archive", { defaultValue: "Archive" })}</button>
              </div>
            </div>
          ))}
      </div>
    </>
  );
}

// ─────────────────────────── Assign document ───────────────────────────

function AssignTab({ token, databases, onError, t }) {
  const [dbSel, setDbSel] = useState("");
  const [archiveNo, setArchiveNo] = useState("");
  const [folders, setFolders] = useState([]);
  const [terms, setTerms] = useState([]);
  const [current, setCurrent] = useState(null);
  const [addTerm, setAddTerm] = useState("");

  useEffect(() => {
    getFolders(token, {}).then((d) => setFolders(Array.isArray(d) ? d : [])).catch(() => {});
    getTaxonomyNodes(token, {}).then((d) => setTerms(Array.isArray(d) ? d : [])).catch(() => {});
  }, [token]);

  const load = async () => {
    if (!dbSel || !archiveNo.trim()) return;
    onError(null);
    try {
      const [f, tr] = await Promise.all([getDocumentFolder(token, Number(dbSel), archiveNo.trim()), getDocumentTerms(token, Number(dbSel), archiveNo.trim())]);
      setCurrent({ folderId: f?.folderId ?? null, folderPath: f?.folderPath ?? null, terms: tr?.terms ?? [] });
    } catch (e) { setCurrent(null); onError(e?.message || t("contentOrg.docNotFound", { defaultValue: "Document not found." })); }
  };
  const setFolder = async (v) => {
    onError(null);
    try { await setDocumentFolder(token, { databaseId: Number(dbSel), archiveNo: archiveNo.trim(), folderId: v ? Number(v) : null }); load(); }
    catch (e) { onError(e?.message || t("contentOrg.saveFailed", { defaultValue: "Save failed." })); }
  };
  const doAddTerm = async () => {
    if (!addTerm) return;
    onError(null);
    try { await addDocumentTerm(token, { databaseId: Number(dbSel), archiveNo: archiveNo.trim(), nodeId: Number(addTerm) }); setAddTerm(""); load(); }
    catch (e) { onError(e?.message || t("contentOrg.saveFailed", { defaultValue: "Save failed." })); }
  };
  const doRemoveTerm = async (nodeId) => {
    onError(null);
    try { await removeDocumentTerm(token, { databaseId: Number(dbSel), archiveNo: archiveNo.trim(), nodeId }); load(); }
    catch (e) { onError(e?.message || t("contentOrg.saveFailed", { defaultValue: "Save failed." })); }
  };

  return (
    <>
      <div className="co-card">
        <div className="co-card__title">📄 {t("contentOrg.tabAssign", { defaultValue: "Assign Document" })}</div>
        <div className="co-grid" style={{ gridTemplateColumns: "1fr 1fr auto" }}>
          <div className="co-field">
            <label className="co-lbl">{t("contentOrg.database", { defaultValue: "Database" })}</label>
            <Dropdown value={dbSel} onChange={setDbSel} placeholder={t("contentOrg.selectDb", { defaultValue: "Select database" })}
              options={databases.map((d) => ({ value: String(dbId(d)), label: dbName(d) }))} />
          </div>
          <div className="co-field">
            <label className="co-lbl">{t("contentOrg.archiveNo", { defaultValue: "Archive number" })}</label>
            <input className="co-input" value={archiveNo} onChange={(e) => setArchiveNo(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} />
          </div>
          <div style={{ display: "flex", alignItems: "end" }}><button className="co-cta" disabled={!dbSel || !archiveNo.trim()} onClick={load}>{t("contentOrg.load", { defaultValue: "Load" })}</button></div>
        </div>
      </div>

      {current && (
        <div className="co-card">
          <div className="co-card__title">📁 {t("contentOrg.folder", { defaultValue: "Folder" })}</div>
          <div style={{ maxWidth: 460 }}>
            <Dropdown value={current.folderId ? String(current.folderId) : ""} onChange={setFolder}
              options={nodeOptions(folders, t, { rootLabel: t("contentOrg.none", { defaultValue: "(none)" }) })}
              placeholder={current.folderPath || t("contentOrg.none", { defaultValue: "(none)" })} />
          </div>

          <div className="co-card__title" style={{ marginTop: 20 }}>🏷️ {t("contentOrg.terms", { defaultValue: "Taxonomy terms" })}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 12 }}>
            {(current.terms || []).length === 0 && <span className="co-node__path" style={{ margin: 0 }}>{t("contentOrg.noTermsAssigned", { defaultValue: "No terms assigned." })}</span>}
            {(current.terms || []).map((tm) => (
              <span key={tm.id} className="co-chip">
                {tm.color && <i className="co-swatch" style={{ width: 8, height: 8, background: tm.color }} />}
                {tm.name}
                <button className="co-chip__x" onClick={() => doRemoveTerm(tm.id)}>✕</button>
              </span>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "end", maxWidth: 460 }}>
            <div style={{ flex: 1 }}>
              <Dropdown value={addTerm} onChange={setAddTerm} placeholder={t("contentOrg.addTerm", { defaultValue: "Add a term…" })}
                options={terms.map((n) => ({ value: String(n.id), label: `${"— ".repeat(n.depth)}${n.name}` }))} />
            </div>
            <button className="co-cta" disabled={!addTerm} onClick={doAddTerm}>{t("contentOrg.add", { defaultValue: "Add" })}</button>
          </div>
        </div>
      )}
    </>
  );
}

// ─────────────────────────── Templates (starter packs) ───────────────────────────

const VERTICAL_ICON = { banking: "🏦", telco: "📡", government: "🏛️", healthcare: "🏥", university: "🎓" };

function TreeList({ nodes }) {
  const rows = [];
  const walk = (arr, depth) => (arr || []).forEach((n) => {
    rows.push(
      <div key={rows.length} className="co-tree__row" style={{ paddingLeft: depth * 16 }}>
        {n.color && <span className="co-tree__sw" style={{ background: n.color }} />}
        <span style={{ opacity: depth ? 0.85 : 1, fontWeight: depth ? 400 : 600 }}>{n.name}</span>
      </div>
    );
    walk(n.children, depth + 1);
  });
  walk(nodes, 0);
  return <>{rows}</>;
}

function TemplatesTab({ token, locale, onError, t }) {
  const [packs, setPacks] = useState([]);
  const [vertical, setVertical] = useState("");
  const [region, setRegion] = useState("");
  const [preview, setPreview] = useState(null);      // { id, data }
  const [applying, setApplying] = useState(null);     // pack id being applied
  const [result, setResult] = useState(null);         // { id, foldersCreated, ... }

  useEffect(() => {
    getTemplates(token, { locale }).then((d) => setPacks(Array.isArray(d) ? d : [])).catch(() => onError(t("contentOrg.loadFailed", { defaultValue: "Failed to load." })));
  }, [token, locale, onError, t]);

  const verticals = Array.from(new Set(packs.map((p) => p.vertical)));
  const regions = Array.from(new Set(packs.map((p) => p.region)));
  const shown = packs.filter((p) => (!vertical || p.vertical === vertical) && (!region || p.region === region));

  const openPreview = async (id) => {
    if (preview?.id === id) { setPreview(null); return; }
    try { const d = await getTemplate(token, id, locale); setPreview({ id, data: d }); }
    catch (e) { onError(e?.message || t("contentOrg.loadFailed", { defaultValue: "Failed to load." })); }
  };
  const doApply = async (id) => {
    setApplying(id); setResult(null); onError(null);
    try { const r = await applyTemplate(token, id, { locale }); setResult({ id, ...r }); }
    catch (e) { onError(e?.message || t("contentOrg.saveFailed", { defaultValue: "Save failed." })); }
    finally { setApplying(null); }
  };

  return (
    <>
      <div className="co-toolbar">
        <div style={{ width: 180 }}>
          <Dropdown value={vertical} onChange={setVertical} placeholder={t("contentOrg.allVerticals", { defaultValue: "All industries" })}
            options={[{ value: "", label: t("contentOrg.allVerticals", { defaultValue: "All industries" }) }, ...verticals.map((v) => ({ value: v, label: `${VERTICAL_ICON[v] || "•"}  ${v.charAt(0).toUpperCase() + v.slice(1)}` }))]} />
        </div>
        <div style={{ width: 150 }}>
          <Dropdown value={region} onChange={setRegion} placeholder={t("contentOrg.allRegions", { defaultValue: "All regions" })}
            options={[{ value: "", label: t("contentOrg.allRegions", { defaultValue: "All regions" }) }, ...regions.map((r) => ({ value: r, label: r }))]} />
        </div>
        <span className="co-count">{shown.length}</span>
      </div>

      {result && (
        <div className="co-preview" style={{ borderColor: "#bbf7d0", background: "#f0fdf4" }}>
          <span className="co-status co-status--on"><i className="co-status__dot" />{t("contentOrg.applied", { defaultValue: "Applied" })}</span>
          <span style={{ marginLeft: 10, fontSize: 13, color: "#166534" }}>
            +{result.foldersCreated} {t("contentOrg.tabFolders", { defaultValue: "Folders" })} · +{result.termsCreated} {t("contentOrg.terms", { defaultValue: "Taxonomy terms" })} · +{result.typesCreated} {t("contentOrg.tabTypes", { defaultValue: "Content Types" })}
            {(result.foldersExisting || result.termsExisting) ? ` · ${result.foldersExisting + result.termsExisting} ${t("contentOrg.alreadyPresent", { defaultValue: "already present" })}` : ""}
          </span>
        </div>
      )}

      {preview && preview.data && (
        <div className="co-preview">
          <div className="co-card__title" style={{ marginBottom: 14 }}>{VERTICAL_ICON[preview.data.vertical]} {preview.data.name} · {preview.data.region}</div>
          <div className="co-preview__cols">
            <div><h4>🗂️ {t("contentOrg.tabFolders", { defaultValue: "Folders" })}</h4><TreeList nodes={preview.data.folders} /></div>
            <div><h4>🏷️ {t("contentOrg.terms", { defaultValue: "Taxonomy terms" })}</h4><TreeList nodes={preview.data.terms} /></div>
            <div><h4>📁 {t("contentOrg.tabTypes", { defaultValue: "Content Types" })}</h4>
              {(preview.data.contentTypes || []).map((c, i) => (
                <div key={i} className="co-tree__row">{c.icon || "📄"} {c.name}{c.defaultClassification ? <span className="co-chip" style={{ marginLeft: 6 }}>{c.defaultClassification}</span> : null}</div>
              ))}
            </div>
          </div>
        </div>
      )}

      {shown.length === 0
        ? <div className="co-tablecard"><div className="co-empty"><div className="co-empty__ring">📦</div><div className="co-empty__title">{t("contentOrg.noTemplates", { defaultValue: "No templates match" })}</div></div></div>
        : (
          <div className="co-tplgrid">
            {shown.map((p) => (
              <div key={p.id} className="co-tpl">
                <div className="co-tpl__head">
                  <span className="co-tpl__ico">{VERTICAL_ICON[p.vertical] || "📦"}</span>
                  <div><div className="co-tpl__name">{p.name}</div><span className="co-tpl__region">{p.region}</span></div>
                </div>
                <div className="co-tpl__desc">{p.description}</div>
                <div className="co-tpl__meta">
                  <span className="co-tpl__stat">🗂️ {p.folderCount}</span>
                  <span className="co-tpl__stat">🏷️ {p.termCount}</span>
                  <span className="co-tpl__stat">📁 {p.contentTypeCount}</span>
                </div>
                <div className="co-tpl__actions">
                  <button className="co-ghost" onClick={() => openPreview(p.id)}>{preview?.id === p.id ? t("contentOrg.hidePreview", { defaultValue: "Hide" }) : t("contentOrg.preview", { defaultValue: "Preview" })}</button>
                  <button className="co-cta" disabled={applying === p.id} onClick={() => doApply(p.id)}>{applying === p.id ? t("contentOrg.applying", { defaultValue: "Applying…" }) : t("contentOrg.applyToOrg", { defaultValue: "Apply to my org" })}</button>
                </div>
              </div>
            ))}
          </div>
        )}
    </>
  );
}

// ─────────────────────────── Clear / reset (danger zone) ───────────────────────────

function ClearPanel({ token, onError, t }) {
  const [sum, setSum] = useState(null);
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState({ folders: true, taxonomy: true, contentTypes: true, docs: false });
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const load = useCallback(() => {
    getContentOrgSummary(token).then(setSum).catch(() => {});
  }, [token]);
  useEffect(() => { load(); }, [load]);

  if (!sum) return null;
  const org = sum.organization;
  const nothing = !scope.folders && !scope.taxonomy && !scope.contentTypes;

  const run = async () => {
    setBusy(true); onError(null); setDone(null);
    try {
      const r = await clearContentOrg(token, {
        organization: org, folders: scope.folders, taxonomy: scope.taxonomy,
        contentTypes: scope.contentTypes, clearDocumentAssignments: scope.docs, confirm,
      });
      setDone(r); setOpen(false); setConfirm(""); load();
    } catch (e) { onError(e?.message || t("contentOrg.clearFailed", { defaultValue: "Clear failed." })); }
    finally { setBusy(false); }
  };

  return (
    <div className="co-danger-card">
      <div className="co-danger-card__title">⚠️ {t("contentOrg.dangerZone", { defaultValue: "Danger zone — clear content organization" })}</div>
      <div className="co-measure">
        <div className="co-measure__item"><b>{sum.folders}</b><span>{t("contentOrg.tabFolders", { defaultValue: "Folders" })}</span></div>
        <div className="co-measure__item"><b>{sum.terms}</b><span>{t("contentOrg.terms", { defaultValue: "Taxonomy terms" })}</span></div>
        <div className="co-measure__item"><b>{sum.contentTypes}</b><span>{t("contentOrg.tabTypes", { defaultValue: "Content Types" })}</span></div>
        <div className="co-measure__item"><b>{sum.taxonomyAssignments}</b><span>{t("contentOrg.assignments", { defaultValue: "Assignments" })}</span></div>
      </div>
      {done && (
        <div className="co-preview" style={{ borderColor: "#bbf7d0", background: "#f0fdf4", marginBottom: 12 }}>
          <span className="co-status co-status--on"><i className="co-status__dot" />{t("contentOrg.cleared", { defaultValue: "Cleared" })}</span>
          <span style={{ marginLeft: 10, fontSize: 13, color: "#166534" }}>
            −{done.foldersDeleted} {t("contentOrg.tabFolders", { defaultValue: "Folders" })} · −{done.termsDeleted} {t("contentOrg.terms", { defaultValue: "Taxonomy terms" })} · −{done.contentTypesDeleted} {t("contentOrg.tabTypes", { defaultValue: "Content Types" })}
            {done.documentsFolderCleared ? ` · ${done.documentsFolderCleared} ${t("contentOrg.docsUnfoldered", { defaultValue: "documents unfoldered" })}` : ""}
          </span>
        </div>
      )}
      <button className="co-danger" onClick={() => { setDone(null); setOpen(true); }}
        disabled={sum.folders + sum.terms + sum.contentTypes === 0}>
        {t("contentOrg.clearBtn", { defaultValue: "Clear content organization…" })}
      </button>

      {open && (
        <div className="co-modal__backdrop" onMouseDown={(e) => e.target === e.currentTarget && setOpen(false)}>
          <div className="co-modal">
            <div className="co-modal__title">⚠️ {t("contentOrg.clearTitle", { defaultValue: "Clear content organization" })}</div>
            <div className="co-modal__sub">
              {t("contentOrg.clearWarn", { defaultValue: "This permanently removes the selected structures for organization" })} <b>{org}</b>. {t("contentOrg.clearIrreversible", { defaultValue: "This cannot be undone." })}
            </div>
            <label className="co-check"><input type="checkbox" checked={scope.folders} onChange={(e) => setScope({ ...scope, folders: e.target.checked })} /> {t("contentOrg.tabFolders", { defaultValue: "Folders" })} ({sum.folders})</label>
            <label className="co-check"><input type="checkbox" checked={scope.taxonomy} onChange={(e) => setScope({ ...scope, taxonomy: e.target.checked })} /> {t("contentOrg.terms", { defaultValue: "Taxonomy terms" })} ({sum.terms}) · {sum.taxonomyAssignments} {t("contentOrg.assignments", { defaultValue: "Assignments" })}</label>
            <label className="co-check"><input type="checkbox" checked={scope.contentTypes} onChange={(e) => setScope({ ...scope, contentTypes: e.target.checked })} /> {t("contentOrg.tabTypes", { defaultValue: "Content Types" })} ({sum.contentTypes})</label>
            <label className="co-check" style={{ marginTop: 10, color: "#b91c1c" }}><input type="checkbox" checked={scope.docs} onChange={(e) => setScope({ ...scope, docs: e.target.checked })} disabled={!scope.folders} /> {t("contentOrg.clearDocs", { defaultValue: "Also clear folder assignments on documents (nulls FolderId across databases)" })}</label>
            <div style={{ marginTop: 16 }}>
              <div className="co-lbl">{t("contentOrg.typeToConfirm", { defaultValue: "Type the organization name to confirm" })}</div>
              <input className="co-input" placeholder={org} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
            </div>
            <div className="co-modal__row">
              <button className="co-ghost" onClick={() => setOpen(false)}>{t("contentOrg.cancel", { defaultValue: "Cancel" })}</button>
              <button className="co-danger" disabled={busy || nothing || confirm !== org} onClick={run}>
                {busy ? t("contentOrg.clearing", { defaultValue: "Clearing…" }) : t("contentOrg.clearConfirmBtn", { defaultValue: "Permanently clear" })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
